'use client';

// Lets visitors opt in to a Web Push notification when a new blog post is
// published. Self-contained (inline AR/EN), hides itself on unsupported browsers.
import { useEffect, useState } from 'react';
import { BellRing, Check, Loader2 } from 'lucide-react';
import {
    isPushSupported,
    getPermission,
    getCurrentSubscription,
    subscribeVisitor,
    unsubscribeVisitor,
} from '@/lib/services/push';

export function NewPostNotifyButton({ locale }: { locale: string }) {
    const ar = locale === 'ar';

    const [supported, setSupported] = useState(false);
    const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>('unsupported');
    const [subscribed, setSubscribed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setSupported(isPushSupported());
        setPermission(getPermission());
        getCurrentSubscription().then((s) => setSubscribed(!!s)).catch(() => {});
    }, []);

    if (!supported) return null; // nothing to offer on unsupported browsers

    const handleClick = async () => {
        setBusy(true);
        setError(null);
        try {
            if (subscribed) {
                await unsubscribeVisitor();
                setSubscribed(false);
            } else {
                await subscribeVisitor();
                setSubscribed(true);
            }
            setPermission(getPermission());
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : (ar ? 'تعذّر تفعيل الإشعارات' : 'Could not enable notifications'));
        } finally {
            setBusy(false);
        }
    };

    const label = busy
        ? (ar ? 'جارٍ…' : 'Working…')
        : subscribed
            ? (ar ? 'إشعارات المقالات مفعّلة' : 'Subscribed to new posts')
            : (ar ? 'نبّهني بالمقالات الجديدة' : 'Notify me of new posts');

    return (
        <div className="flex flex-col items-center gap-1.5">
            <button
                type="button"
                onClick={handleClick}
                disabled={busy || permission === 'denied'}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border border-primary-500/30 text-primary-600 hover:bg-primary-500/10 transition-colors disabled:opacity-60 cursor-pointer"
            >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" />
                    : subscribed ? <Check className="w-3 h-3" />
                        : <BellRing className="w-3 h-3" />}
                {label}
            </button>
            {permission === 'denied' && (
                <p className="text-xs text-(--muted)">
                    {ar ? 'الإشعارات محظورة في إعدادات المتصفح.' : 'Notifications are blocked in your browser settings.'}
                </p>
            )}
            {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
    );
}
